import { findStopRefusal, openStopBranch } from '@folio/utils/stop-branch';
import { getBranchCloseReason } from '@folio/utils/stop-reasons';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyStopReasons } from '@folio/utils/usePolicyStopReasons';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  branchControllerInvalidateBranch,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerGetPolicyQueryOptions,
  policyControllerStopPolicy,
} from 'korint-sdk';

/**
 * A draft is an open STOP branch the policy carries no stop for yet: once BASE echoes the stop it
 * is scheduled, not drafted. Composed from the branches and the policy the dashboard already holds.
 */
export const useStopDraft = (policyId: string, { enabled = true } = {}) => {
  const { policy, policyOnMain, branches, mutate } = usePolicyOnDashboard();

  const { stopReasonConfigs, isPending: areStopReasonsPending } = usePolicyStopReasons({
    productId: policy.productId,
    policy: {
      startedAt: policy.startedAt,
      nextRenewalAt: policy.nextRenewalAt,
      suspendedAt: policy.suspendedAt,
    },
  });

  const draftBranch = policyOnMain.stop ? undefined : openStopBranch(branches?.data);

  const { data: draftPolicy, isLoading } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId: draftBranch?.id }),
    enabled: enabled && !!draftBranch,
  });

  // Suspense, not useQuery: a pending user would attribute the close to the customer.
  const { data: user } = useSuspenseQuery(legacyPermissionsControllerAtMeQueryOptions());

  const { mutateAsync: save } = useMutation({
    mutationFn: (body: { reason: string; details?: { key: string; value: string }[] }) =>
      policyControllerStopPolicy(policyId, body, { draft: true }),
    onSuccess: () => mutate(),
  });

  // mutate, not mutateAsync: the only caller is a click handler that cannot await, and a rejected
  // invalidation is already surfaced by the shared MutationCache. `save` stays awaitable because
  // its callers gate the step transition on it.
  const { mutate: discard, isPending: isDiscarding } = useMutation({
    mutationFn: (branchId: string) =>
      branchControllerInvalidateBranch(policyId, branchId, {
        reason: getBranchCloseReason(user),
      }),
    onSuccess: () => mutate(),
  });

  const stop = draftBranch && draftPolicy?.stop;
  const refusedBranch = findStopRefusal(draftBranch?.id, branches?.data);

  return {
    draft: stop
      ? {
          branchId: draftBranch.id,
          reason: stop.reason,
          stopDate: stop.stopDate,
          isAwaitingValidation: policyOnMain.isStopAwaitingValidation ?? false,
          requiredDocuments:
            stopReasonConfigs.find(({ reason }) => reason === stop.reason)?.requiredDocuments ?? [],
          // The denial sits on the branch this one forked from, never on the draft itself.
          refusedBranchId: refusedBranch?.id,
        }
      : null,
    // The reason configs gate it too: an empty list reads as "needs no proof" and would resume a
    // dated draft at the recap, which redirects once and never corrects itself.
    isLoading: !!draftBranch && (isLoading || areStopReasonsPending),
    save,
    discard,
    isDiscarding,
  };
};
